'use strict';

import { createAlignmentMethodPromptUI } from './alignment-method-prompt-ui.js';

const ARAuthoringMenuItems = Object.freeze({
    ALIGNMENT_POINT_VISIBILITY: "ARAuthoringAlignmentPointVisibility",
    REDO_ALIGNMENT: "ARAuthoringRedoAlignment",
    CHANGE_ALIGNMENT_POINTS: "ARAuthoringChangeAlignmentPoints",
    ALIGNMENT_MODE: "ARAuthoringAlignmentMode"
});

const ARAlignmentMethod = {
    VIRTUAL: 'virtual',
    PHYSICAL: 'physical'
};

const AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS = 8000;

const PhysicalAlignmentXRText = Object.freeze({
    FIRST_SCENE_POINT: {
        text: 'Position the green dot on the first alignment point, then click the trigger to lock in.',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    },
    SECOND_SCENE_POINT: {
        text: 'Do the same thing for the 2nd alignment point.',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    },
    REALIGN_FIRST_PHYSICAL: {
        text: 'Use the controller trigger to place the green dot on the 1st physical alignment point.',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    },
    REALIGN_SECOND_PHYSICAL: {
        text: 'Do the same thing for the 2nd physical alignment point.',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    }
});

const VirtualAlignmentXRText = Object.freeze({
    MOVEMENT_ANCHOR: {
        text: 'Pick movement anchor on the virtual model',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    },
    ROTATION_ANCHOR: {
        text: 'Pick rotation anchor on the virtual model',
        duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
    }
});
class ARAuthoringController {
    constructor() {
        this.states = {
            IDLE: 'idle',
            ALIGNMENT_SETUP: 'alignment_setup',
            ALIGNMENT_RUNNING: 'alignment_running',
            AUTHORING_ACTIVE: 'authoring_active'
        };
        this.currentState = this.states.IDLE;
        this.isAlignedWithPhysicalSpace = false;
        this.alignmentConfig = null;
        this.previousAlignmentConfig = null;
        this.tempAlignmentData = {
            alignmentMethod: null,
            movementAnchor: null,
            rotationAnchor: null
        };
        this.alignmentModeActive = false;
        this.alignmentInProgress = false;
        this._alignmentPromptUI = null;
        this.anchorPointsVisible = false;
        this.restoreARAuthoringAfterStepCreation = false;

        this.checkControllerInterval = null;
        this.addEventListeners();
    }

    addEventListeners() {
        wv.communicator.addEventListener(text_startARAuthoring, this.handleStartARAuthoring.bind(this));
        wv.communicator.addEventListener(text_loadARAuthoringConfig, this.handleLoadARAuthoringConfig.bind(this));
        if (wv.rootObject) {
            wv.rootObject.addEventListener(ModelEvents.LOAD, this._onModelLoadedForVirtualAlignment.bind(this));
        }
    }

    async _onModelLoadedForVirtualAlignment(event) {
        if (this.tempAlignmentData.alignmentMethod !== ARAlignmentMethod.VIRTUAL) {
            return;
        }

        if (this.currentState === this.states.ALIGNMENT_SETUP) {
            this.setNewVirtualAlignment();
        } else if (this.pendingARAuthoringModelToLoad === event.modelId) {
            this.pendingARAuthoringModelToLoad = null;
            let tryCount = 0;
            if (this.checkControllerInterval != null) {
                clearInterval(this.checkControllerInterval);
                this.checkControllerInterval = null;
            }
            this.checkControllerInterval = setInterval(() => {
                if (ModelAlignController.isControllerAvailable()) {
                    clearInterval(this.checkControllerInterval);
                    this.checkControllerInterval = null;
                    this.performVirtualAlignment(this.alignmentConfig);
                } else if (tryCount >= 10) {
                    clearInterval(this.checkControllerInterval);
                    this.checkControllerInterval = null;
                }
                tryCount++;
            }, 500);
        }
    }

    setState(newState) {
        this.currentState = newState;
        this.onStateEnter(newState);
    }

    onStateEnter(state) {
        switch (state) {
            case this.states.IDLE:
                this.updateARAuthoringMenuVisibility(false);
                this._cleanupAlignmentMethodPromptUI();
                break;
            case this.states.AUTHORING_ACTIVE:
                this.enableAuthoringMode();
                this.updateAnchorPointsVisibility();
                this.updateARAuthoringMenuVisibility(true);
                break;
        }
    }

    async handleStartARAuthoring([]) {
        try {
            const prereqError = await this._ensureARAuthoringPrerequisites();
            if (prereqError) {
                return prereqError;
            }
            if (this.alignmentConfig && this._isValidPoint(this.alignmentConfig.movementAnchor.point) && this._isValidPoint(this.alignmentConfig.rotationAnchor.point)) {
                if (this.alignmentConfig.alignmentMethod === ARAlignmentMethod.PHYSICAL) {
                    await this.performPhysicalAlignment(this.alignmentConfig);
                } else if (this.alignmentConfig.alignmentMethod === ARAlignmentMethod.VIRTUAL) {
                    const prepareResult = await this._prepareVirtualAlignmentModel(this.alignmentConfig);
                    if (prepareResult.startsWith('error:')) {
                        return prepareResult;
                    }
                    if (prepareResult === 'ready') {
                        await this.performVirtualAlignment(this.alignmentConfig);
                    }
                } else {
                    console.error('Invalid alignment method:', this.alignmentConfig.alignmentMethod);
                }
                return 'success:AR Authoring started';
            }
            this.previousAlignmentConfig = null;
            this.alignmentConfig = null;
            this.setState(this.states.ALIGNMENT_SETUP);
            this.promptAlignmentMethod();
            return 'success:AR Authoring started';
        } catch (error) {
            console.error('Error starting AR authoring:', error);
            return 'error:' + error.message;
        }
    }

    _isValidPoint(point) {
        return point && point.x !== undefined && point.y !== undefined && point.z !== undefined &&
            !isNaN(point.x) && !isNaN(point.y) && !isNaN(point.z);
    }

    async _prepareVirtualAlignmentModel(alignmentConfig) {
        this.tempAlignmentData.alignmentMethod = ARAlignmentMethod.VIRTUAL;
        const modelId = alignmentConfig.movementAnchor.modelId;
        const model = getModelObject(modelId);
        if (model) {
            if (!model.visible) {
                changeThisModelVisibility(modelId, true);
            }
            return 'ready';
        }
        const modelList = SceneMenuController.getResourceListData('Model', {});
        const resourceIds = SceneMenuController.getAvailableResourceIds(modelList);
        if (resourceIds.includes(modelId)) {
            const modelItem = SceneMenuController.findItemById(modelList, modelId);
            if (modelItem) {
                const name = modelItem.name;
                const modeller = modelItem.core?.modeller || ModelData.getModellerName(name, modelId);
                const version = modelItem.version;
                this.pendingARAuthoringModelToLoad = modelId;
                await model_load_helper.loadModelByModeller(name, modeller, modelId, version);
                return 'pending_load';
            }
        }
        VRMessageController.addMessage({
            text: 'Model not found. Exiting AR. Please add a model to the training and try again.',
            duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
        });
        setTimeout(() => {
            VR.exitFromVRMode();
        }, 2000);
        return 'error:Model not found';
    }

    _pointToVector3(point) {
        if (!this._isValidPoint(point)) return null;
        if (point.isVector3) return point;
        return new THREE.Vector3(point.x, point.y, point.z);
    }

    handleLoadARAuthoringConfig(arg) {
        const configJson = Array.isArray(arg) ? arg[0] : arg;
        try {
            const config = configJson == null || typeof configJson === 'object' ? configJson : JSON.parse(configJson);
            if (config == null) {
                this.alignmentConfig = null;
                return 'success:Config cleared';
            }
            if (config.movementAnchor?.point) {
                config.movementAnchor.point = this._pointToVector3(config.movementAnchor.point);
            }
            if (config.rotationAnchor?.point) {
                config.rotationAnchor.point = this._pointToVector3(config.rotationAnchor.point);
            }
            this.alignmentConfig = config;

            return 'success:Config loaded';
        } catch (error) {
            console.error('Error loading AR authoring config:', error);
            return 'error:' + error.message;
        }
    }

    loadARAuthoringConfigFromTrainingData(trainingData) {
        const config = trainingData.ARAlignmentConfig;
        if (config) {
            if (config.movementAnchor?.model) {
                const res = trainingData.resources.find(r => r.id === config.movementAnchor.model);
                config.movementAnchor.modelId = res ? res.resourceId : null;
                if (config.movementAnchor.model) {
                    delete config.movementAnchor.model;
                }
            }
            if (config.rotationAnchor?.model) {
                const res = trainingData.resources.find(r => r.id === config.rotationAnchor.model);
                config.rotationAnchor.modelId = res ? res.resourceId : null;
                if (config.rotationAnchor.model) {
                    delete config.rotationAnchor.model;
                }
            }
            this.handleLoadARAuthoringConfig(config);
            if (this.restoreARAuthoringAfterStepCreation) {
                this.restoreARAuthoringAfterStepCreation = false;
                this.onMenuAlignmentModeToggle(true);
            }
        }
    }

    isValidOperation() {
        if (iq3XRSession.isSessionRunning() && iq3XRSession.mode.includes(SessionMode.VR)) {
            VRMessageController.addMessage({
                text: "Exit VR mode before starting AR authoring.",
                duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
            });
            return false;
        }

        return true;
    }

    async _ensureARAuthoringPrerequisites() {
        if (isMobileDevice()) {
            VRMessageController.addMessage({
                text: 'Author in AR is only supported on HMD devices (head-mounted displays), not on mobile.',
                duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
            });
            return 'error:Author in AR is only supported on HMD devices, not on mobile.';
        }
        if (!this.isValidOperation()) {
            return 'error:Prerequisites not met';
        }
        const enteredAR = await ModelAlignController.enterAR(false);
        if (!enteredAR) {
            VRMessageController.addMessage({
                text: "Failed to enter AR mode. AR Authoring requires AR session.",
                duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
            });
            return 'error:Failed to enter AR';
        }

        hideAllVRmenu();
        return null;
    }

    promptAlignmentMethod() {
        this.createAlignmentMethodPromptUI();
    }

    async startReAlignment() {
        const method = this.alignmentConfig.alignmentMethod;
        VRMessageController.addMessage({
            text: `Starting ${method} alignment re-alignment...`,
            duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
        });

        if (method === ARAlignmentMethod.PHYSICAL) {
            await this.performPhysicalAlignment(this.alignmentConfig);
        } else if (method === ARAlignmentMethod.VIRTUAL) {
            const prepareResult = await this._prepareVirtualAlignmentModel(this.alignmentConfig);
            if (prepareResult.startsWith('error:')) {
                return;
            }
            if (prepareResult === 'ready') {
                await this.performVirtualAlignment(this.alignmentConfig);
            }
        }
    }

    saveAlignmentConfig() {
        const config = {
            alignmentMethod: this.tempAlignmentData.alignmentMethod,
            movementAnchor: this.tempAlignmentData.movementAnchor,
            rotationAnchor: this.tempAlignmentData.rotationAnchor
        };
        this.alignmentConfig = config;
        wv.communicator.socket.communicateWithContainer({
            message: text_saveARAuthoringConfig.command,
            data: JSON.stringify(config)
        });
    }

    enableAlignmentMode() {
        this.alignmentModeActive = true;

        wv.effectController.lockUserMovement = true;
        wv.effectController.lockUserOrientation = true;

        VR.mouseControls.enablePan = false;
        VR.mouseControls.enableRotate = false;

        VRMessageController.addMessage({
            text: "Alignment mode enabled. Joysticks and camera movement are locked.",
            duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
        });
    }

    disableAlignmentMode() {
        this.isAlignedWithPhysicalSpace = false;
        this.alignmentModeActive = false;

        wv.effectController.lockUserMovement = false;
        wv.effectController.lockUserOrientation = false;

        resetMovementFeatureActivationStatus();
    }

    createAlignmentMethodPromptUI() {
        if (this._alignmentPromptUI) {
            return;
        }
        this._alignmentPromptUI = createAlignmentMethodPromptUI({
            onChoice: (method) => this.onAlignmentMethodChoice(method)
        });
    }

    _cleanupAlignmentMethodPromptUI() {
        if (this._alignmentPromptUI) {
            this._alignmentPromptUI.remove();
            this._alignmentPromptUI = null;
        }
    }

    async onAlignmentMethodChoice(method) {
        this._cleanupAlignmentMethodPromptUI();

        if (method === 'cancel') {
            this.cancelARAuthoring();
            return;
        }

        this.disableAlignmentMode();
        if (method === ARAlignmentMethod.VIRTUAL) {
            const isServerLess = isServerLessTrainingUIMeeting();
            this.tempAlignmentData.alignmentMethod = ARAlignmentMethod.VIRTUAL;

            let hasModelsInResources = false;
            if (isServerLess) {
                try {
                    const modelList = SceneMenuController.getResourceListData('Model', {});
                    const resourceIds = SceneMenuController.getAvailableResourceIds(modelList);
                    hasModelsInResources = Array.isArray(resourceIds) && resourceIds.length > 0;
                } catch (e) {
                    hasModelsInResources = false;
                }
            } else {
                const cacheFiles = wv.effectController && wv.effectController.meetingInfo && wv.effectController.meetingInfo.data && wv.effectController.meetingInfo.data.cacheFiles;
                const availableList = wv.availableFileList && wv.availableFileList.available_file_list_array;
                hasModelsInResources = (Array.isArray(cacheFiles) && cacheFiles.length > 0) ||
                    (Array.isArray(availableList) && availableList.length > 0);
            }

            if (!hasModelsInResources) {
                VRMessageController.addMessage({
                    text: 'No model is loaded. Add a model to resources and try again or try with physical object.',
                    duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
                });
                setTimeout(() => {
                    this.handleStartARAuthoring([]);
                }, 1000);
                return;
            }

            const alreadyHasModelInScene = modelManager.getNumberOfModelLoadedInScene() > 0;

            if (alreadyHasModelInScene) {
                this.setNewVirtualAlignment();
                return;
            }

            wv.availableFileList.requestForAvailableFile();
            if (isServerLess) {
                try {
                    await SceneMenuController.updateAvailableModelListMenu({});
                } catch (err) {
                    console.log('updateAvailableModelListMenu failed', err);
                }
            }
            const parent = get_vr_menu_parent() || iq3Camera.camera;
            if (parent) {
                update_menu_data_based_on_parent(parent);
                showVRMenu(parent);
                mController.resetSavedMenuState();
                mController.findAndShowThisMenuView(AvailableFilesMenu.menuItemName, parent, false);
            }

            VRMessageController.addMessage({
                text: 'Load a model from the menu, then pick alignment points on the virtual model and physical space.',
                duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
            });
            return;
        }

        if (method === ARAlignmentMethod.PHYSICAL) {
            this.tempAlignmentData.alignmentMethod = ARAlignmentMethod.PHYSICAL;
            this.setNewPhysicalAlignment();
            return;
        }
    }

    _onSetNewAlignment() {
        if (!this.anchorPointsVisible) {
            this.hideAllAnchorPoints();
        }
        this.saveAlignmentConfig();
        this.setState(this.states.AUTHORING_ACTIVE);
        this.alignmentInProgress = false;
        this.isAlignedWithPhysicalSpace = true;
    }

    _onPerformAlignment() {
        this.anchorPointsVisible = true;
        checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_POINT_VISIBILITY, this.anchorPointsVisible);
        this.setState(this.states.AUTHORING_ACTIVE);
        this.alignmentInProgress = false;
        this.isAlignedWithPhysicalSpace = true;
    }

    _onAlignmentError(error, contextLabel) {
        console.error(contextLabel || 'AR alignment error:', error);
        this.hideAllAnchorPoints();
        this.alignmentInProgress = false;

        const config = this.alignmentConfig;
        if (config) {
            const resetTransparency = (modelId) => {
                if (modelId) {
                    wv.ChangeTransparency(modelId, modelId, 1, false, false);
                }
            };
            resetTransparency(config.movementAnchor?.modelId);
            const rotId = config.rotationAnchor?.modelId;
            if (rotId && rotId !== config.movementAnchor?.modelId) {
                resetTransparency(rotId);
            }
        }

        VRMessageController.addMessage({
            text: 'Alignment failed. You can try re-alignment from the AR Authoring menu.',
            duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
        });

        if (this.alignmentConfig) {
            this.setState(this.states.AUTHORING_ACTIVE);
            this.updateAnchorPointsVisibility();
        } else {
            this.setState(this.states.ALIGNMENT_SETUP);
            this.promptAlignmentMethod();
        }
    }

    setAnchorData(targetAnchor, data) {
        if (data.modelId) {
            targetAnchor.modelId = data.modelId;
        }

        if (!data.point) {
            return;
        }

        const p = data.point;
        if (p.isVector3 && typeof p.clone === 'function') {
            targetAnchor.point = p.clone();
            return;
        }
        if (this._isValidPoint(p)) {
            targetAnchor.point = new THREE.Vector3(p.x, p.y, p.z);
            return;
        }
    }

    getLocalToWorldAnchorData(anchor) {
        const point = new THREE.Vector3(anchor.point.x, anchor.point.y, anchor.point.z);
        const modelId = anchor.modelId;
        if (modelId) {
            const modelObject = getModelObject(modelId);
            if (modelObject) {
                return { point: modelObject.localToWorld(point.clone()), modelId };
            }
        }
        return anchor;
    }

    getWorldToLocalAnchorData(anchor) {
        const point = new THREE.Vector3(anchor.point.x, anchor.point.y, anchor.point.z);
        const modelId = anchor.modelId;
        if (modelId) {
            const modelObject = getModelObject(modelId);
            if (modelObject) {
                return { point: modelObject.worldToLocal(point.clone()), modelId };
            }
        }
        return anchor;
    }

    async _withVirtualModelTransparency(modelId, asyncFn) {
        if (!modelId) {
            return asyncFn();
        }
        wv.ChangeTransparency(modelId, modelId, 1, false, true);
        try {
            return await asyncFn();
        } finally {
            wv.ChangeTransparency(modelId, modelId, 1, false, false);
        }
    }

    async _runVirtualPhysicalPositionStep(anchorData) {
        await this._withVirtualModelTransparency(anchorData.modelId, async () => {
            await ModelAlignController.startPhysicalPositionAlignment(
                this.getLocalToWorldAnchorData(anchorData)
            );
        });
    }

    async _runVirtualPhysicalRotationStep(anchorData) {
        await this._withVirtualModelTransparency(anchorData.modelId, async () => {
            await ModelAlignController.startPhysicalRotationAlignment(
                this.getLocalToWorldAnchorData(anchorData)
            );
        });
    }

    async _runVirtualAnchorAlignmentPipeline({ movementAnchor, rotationAnchor, confirmMessage, onConfirmYes, onRetry }) {
        await this._runVirtualPhysicalPositionStep(movementAnchor);
        await this._runVirtualPhysicalRotationStep(rotationAnchor);
        this._showAlignmentConfirmPrompt(confirmMessage, onConfirmYes, onRetry);
    }

    async _runPhysicalAnchorAlignmentPipeline({ movementAnchor, rotationAnchor, confirmMessage, onConfirmYes, onRetry }) {
        await ModelAlignController.startPhysicalPositionAlignment(
            this.getLocalToWorldAnchorData(movementAnchor),
            {
                VRMessage: PhysicalAlignmentXRText.REALIGN_FIRST_PHYSICAL
            }
        );
        await ModelAlignController.startPhysicalSceneRotationAlignment(
            this.getLocalToWorldAnchorData(rotationAnchor),
            {
                VRMessage: PhysicalAlignmentXRText.REALIGN_SECOND_PHYSICAL
            }
        );
        this._showAlignmentConfirmPrompt(confirmMessage, onConfirmYes, onRetry);
    }

    async setNewVirtualAlignment() {
        this.setState(this.states.ALIGNMENT_RUNNING);
        this.alignmentInProgress = true;
        this.tempAlignmentData.alignmentMethod = ARAlignmentMethod.VIRTUAL;
        this.tempAlignmentData.movementAnchor = {};
        this.tempAlignmentData.rotationAnchor = {};

        this.hideAllAnchorPoints();

        try {
            let virtualData = await ModelAlignController.startVirtualModelPointPick(true, {
                VRMessage: VirtualAlignmentXRText.MOVEMENT_ANCHOR,
                anchorType: AnchorTypeEnum.MOVEMENT_ANCHOR,
                showPoint: true,
                fixedscale: true,
            });

            if (!virtualData) {
                this.alignmentInProgress = false;
                return;
            }
            virtualData = this.getWorldToLocalAnchorData(virtualData);
            this.setAnchorData(this.tempAlignmentData.movementAnchor, virtualData);
            await this._runVirtualPhysicalPositionStep(this.tempAlignmentData.movementAnchor);

            virtualData = await ModelAlignController.startVirtualModelPointPick(true, {
                VRMessage: VirtualAlignmentXRText.ROTATION_ANCHOR,
                anchorType: AnchorTypeEnum.ROTATION_ANCHOR,
                showPoint: true,
                fixedscale: true,
            });

            if (!virtualData) {
                this.alignmentInProgress = false;
                return;
            }

            virtualData = this.getWorldToLocalAnchorData(virtualData);
            this.setAnchorData(this.tempAlignmentData.rotationAnchor, virtualData);
            await this._runVirtualPhysicalRotationStep(this.tempAlignmentData.rotationAnchor);

            this._showAlignmentConfirmPrompt(
                'Do you want to confirm the alignment?',
                () => this._onSetNewAlignment(),
                () => this.setNewVirtualAlignment()
            );
        } catch (err) {
            this._onAlignmentError(err, 'setNewVirtualAlignment');
        }
    }

    async performVirtualAlignment(config) {
        this.setState(this.states.ALIGNMENT_RUNNING);
        this.alignmentInProgress = true;

        try {
            this.hideAllAnchorPoints();
            this._showAnchorPoint(AnchorTypeEnum.MOVEMENT_ANCHOR, true, config.movementAnchor);
            this._showAnchorPoint(AnchorTypeEnum.ROTATION_ANCHOR, true, config.rotationAnchor);

            await this._runVirtualAnchorAlignmentPipeline({
                movementAnchor: config.movementAnchor,
                rotationAnchor: config.rotationAnchor,
                confirmMessage: 'Do you want to confirm the alignment?',
                onConfirmYes: () => this._onPerformAlignment(),
                onRetry: () => this.performVirtualAlignment(config)
            });
        } catch (err) {
            this._onAlignmentError(err, 'performVirtualAlignment');
        }
    }

    _showAlignmentConfirmPrompt(text, positiveCallback, negativeCallback) {
        wv.promptWindow3dObject.changeText(text, "Yes", "No");
        wv.promptWindow3dObject.askNow((value) => {
            if (value) {
                positiveCallback();
            } else {
                negativeCallback();
            }
        });
    }

    async setNewPhysicalAlignment() {
        this.setState(this.states.ALIGNMENT_RUNNING);
        this.alignmentInProgress = true;
        this.tempAlignmentData.alignmentMethod = ARAlignmentMethod.PHYSICAL;
        this.tempAlignmentData.movementAnchor = {};
        this.tempAlignmentData.rotationAnchor = {};

        this.hideAllAnchorPoints();

        try {
            let sceneData = await ModelAlignController.startScenePointPick(true, {
                VRMessage: PhysicalAlignmentXRText.FIRST_SCENE_POINT,
                anchorType: AnchorTypeEnum.MOVEMENT_ANCHOR,
                showPoint: true,
                fixedscale: true,
            });

            if (!sceneData) {
                this.alignmentInProgress = false;
                return;
            }
            this.setAnchorData(this.tempAlignmentData.movementAnchor, sceneData);

            sceneData = await ModelAlignController.startScenePointPick(true, {
                VRMessage: PhysicalAlignmentXRText.SECOND_SCENE_POINT,
                anchorType: AnchorTypeEnum.ROTATION_ANCHOR,
                showPoint: true,
                fixedscale: true,
            });

            if (!sceneData) {
                this.alignmentInProgress = false;
                return;
            }
            this.setAnchorData(this.tempAlignmentData.rotationAnchor, sceneData);

            this._showAlignmentConfirmPrompt(
                'Do you want to confirm the physical alignment?',
                () => this._onSetNewAlignment(),
                () => this.setNewPhysicalAlignment()
            );
        } catch (err) {
            this._onAlignmentError(err, 'setNewPhysicalAlignment');
        }
    }

    async performPhysicalAlignment(config) {
        this.setState(this.states.ALIGNMENT_RUNNING);
        this.alignmentInProgress = true;

        try {
            this.hideAllAnchorPoints();
            this._showAnchorPoint(AnchorTypeEnum.MOVEMENT_ANCHOR, true, config.movementAnchor);
            this._showAnchorPoint(AnchorTypeEnum.ROTATION_ANCHOR, true, config.rotationAnchor);

            await this._runPhysicalAnchorAlignmentPipeline({
                movementAnchor: config.movementAnchor,
                rotationAnchor: config.rotationAnchor,
                confirmMessage: 'Do you want to confirm the physical alignment?',
                onConfirmYes: () => this._onPerformAlignment(),
                onRetry: () => this.performPhysicalAlignment(config)
            });
        } catch (err) {
            this._onAlignmentError(err, 'performPhysicalAlignment');
        }
    }

    enableAuthoringMode() {
        checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_MODE, true);
        this.enableAlignmentMode();

        VRMessageController.addMessage({
            text: "AR Authoring active! You can now author training content.",
            duration: AR_AUTHORING_ALIGNMENT_VR_MESSAGE_MS
        });
    }

    cancelARAuthoring() {
        this._cleanupAlignmentMethodPromptUI();
        if (this.previousAlignmentConfig && !this.alignmentConfig) {
            this.alignmentConfig = this.previousAlignmentConfig;
        }
        this.previousAlignmentConfig = null;
        if (this.alignmentModeActive && !this.alignmentConfig) {
            this.disableAlignmentMode();
        }
        this.updateAnchorPointsVisibility();
        this.alignmentInProgress = false;
        VRMessageController.addMessage({text: 'AR Authoring cancelled', duration: 5000});
        if (!this.alignmentConfig) {
            VR.exitFromVRMode();
        }
    }

    onARExit() {
        this._cleanupAlignmentMethodPromptUI();
        if (iq3XRSession.mode.includes(SessionMode.AR)) return;
        if (this.checkControllerInterval != null) {
            clearInterval(this.checkControllerInterval);
            this.checkControllerInterval = null;
        }
        this.alignmentInProgress = false;
        if (this.alignmentModeActive) {
            checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_MODE, false);
            this.disableAlignmentMode();
        }
        this.updateARAuthoringMenuVisibility(false);
        this.setState(this.states.IDLE);
    }

    _showAnchorPoint(anchorType, visibility, anchorData) {
        anchorPointController.showAnchorPoint([
            anchorType, 
            anchorData.modelId, 
            anchorData.point, 
            visibility
        ], true);
    }

    hideAllAnchorPoints() {
        anchorPointController.showAnchorPoint([
            AnchorTypeEnum.MOVEMENT_ANCHOR,
            '',
            {x: 0, y: 0, z: 0},
            false
        ], true);
        anchorPointController.showAnchorPoint([
            AnchorTypeEnum.ROTATION_ANCHOR,
            '',
            {x: 0, y: 0, z: 0},
            false
        ], true);
        return true;
    }

    showAllAnchorPoints() {
        if (!this.alignmentConfig || !this.anchorPointsVisible) return false;

        if (this.alignmentConfig.alignmentMethod === ARAlignmentMethod.VIRTUAL) {
            const modelId = this.alignmentConfig.movementAnchor?.modelId;
            if (!modelId || !getModelObject(modelId)) {
                VRMessageController.addMessage({
                    text: 'Model not found. Alignment points cannot be shown.',
                    duration: 5000
                });
                return false;
            }
        }

        const movementAnchor = this.alignmentConfig.movementAnchor;
        const rotationAnchor = this.alignmentConfig.rotationAnchor;
        if (this._isValidPoint(movementAnchor?.point) && this._isValidPoint(rotationAnchor?.point)) {
            this._showAnchorPoint(AnchorTypeEnum.MOVEMENT_ANCHOR, true, movementAnchor);
            this._showAnchorPoint(AnchorTypeEnum.ROTATION_ANCHOR, true, rotationAnchor);
            return true;
        }
        return false;
    }

    updateAnchorPointsVisibility() {
        if (this.anchorPointsVisible) {
            return this.showAllAnchorPoints();
        } else {
            return this.hideAllAnchorPoints();
        }
    }

    initMenu(controller, prop) {
        try {
            if (!this._arAuthoringMenu) {
                this._arAuthoringMenu = new MenuData(ARAuthoringMenu.text, 4, 4);
                wv.ARAuthoringMenuController = new MenuController(this._arAuthoringMenu);
                wv.ARAuthoringMenuController.setMenuControlParameter(VR.scene, iq3Camera.camera, wv.selectionSphere);
                parseChildrenItems(wv.ARAuthoringMenuController, prop.children, menuItemMaps.ARAuthoringMenuItemMap);
            }
            this._arAuthoringMenu.isVisible = prop.isVisible;
            this._arAuthoringMenu.isPresenter = prop.isPresenter;
            this._arAuthoringMenu.isViewer = prop.isViewer;
            controller.appendMenuItem(ARAuthoringMenu.menuItemName, prop.text || ARAuthoringMenu.text, this._arAuthoringMenu);
        } catch (error) {
            console.error('Error in AR authoring menu:', error);
        }
    }

    onMenuAlignmentPointVisibility(value) {
        this.anchorPointsVisible = !!value;
        checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_POINT_VISIBILITY, this.anchorPointsVisible);
        const success = this.updateAnchorPointsVisibility();
        if (success) {
            VRMessageController.addMessage({
                text: value ? 'AR Authoring: Alignment points visible' : 'AR Authoring: Alignment points hidden',
                duration: 5000
            });
        } else {
            VRMessageController.addMessage({
                text: 'Failed to update alignment points visibility',
                duration: 5000
            });
        }
    }

    async onMenuRedoAlignment() {
        const prereqError = await this._ensureARAuthoringPrerequisites();
        if (prereqError) {
            return;
        }
        if (!this.alignmentConfig) {
            VRMessageController.addMessage({ text: 'No alignment config to re-align.', duration: 5000 });
            return;
        }
        const movementAnchor = this.alignmentConfig.movementAnchor;
        const rotationAnchor = this.alignmentConfig.rotationAnchor;
        if (!this._isValidPoint(movementAnchor?.point) || !this._isValidPoint(rotationAnchor?.point)) {
            VRMessageController.addMessage({ text: 'Invalid alignment points. Alignment points cannot be re-aligned.', duration: 5000 });
            return;
        }
        await this.startReAlignment();
        this.anchorPointsVisible = true;
        checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_POINT_VISIBILITY, this.anchorPointsVisible);
    }

    async onMenuChangeAlignmentPoints() {
        if (wv.promptWindow3dObject.isPromptWindowVisible()) return;
        const prereqError = await this._ensureARAuthoringPrerequisites();
        if (prereqError) {
            return;
        }
        if (!this.alignmentConfig){
            VRMessageController.addMessage({ text: 'No existing AR authoring config to change alignment points.', duration: 5000 });
            return; 
        }
        if (this.alignmentInProgress){
            VRMessageController.addMessage({ text: 'Alignment in progress. Please complete the alignment first.', duration: 5000 });
            return;
        }
        const ctrl = this;
        wv.promptWindow3dObject.changeText(
            'Do you want to change the alignment points? Current alignment points will be lost.',
            'Yes',
            'No'
        );
        wv.promptWindow3dObject.askNow((value) => {
            if (!value) return;
            ctrl.previousAlignmentConfig = ctrl.alignmentConfig;
            ctrl.alignmentConfig = null;
            ctrl.alignmentInProgress = false;
            ctrl.setState(ctrl.states.ALIGNMENT_SETUP);
            ctrl.promptAlignmentMethod();
        });
    }

    onMenuAlignmentModeToggle(value) {
        if (value) {
            if (iq3XRSession.isSessionRunning() && iq3XRSession.mode.includes(SessionMode.AR)) {
                this.enableAlignmentMode();
            } else {
                this.alignmentModeActive = false;
                checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_MODE, false);
            }
            
            return;
        }
        if (this.isAlignedWithPhysicalSpace) {
            const ctrl = this;
            wv.promptWindow3dObject.changeText(
                'This could cause mis-alignment between the virtual and physical spaces. Do you wish to continue?',
                'Yes',
                'No'
            );
            wv.promptWindow3dObject.askNow((value) => {
                if (value) {
                    ctrl.disableAlignmentMode();
                    VRMessageController.addMessage({
                        text: "Alignment mode disabled. Joysticks and camera movement are unlocked.",
                        duration: 5000
                    });
                } else {
                    checkBoxSyncVR(ARAuthoringMenuItems.ALIGNMENT_MODE, true);
                }
            });
        } else {
            this.disableAlignmentMode();
            VRMessageController.addMessage({
                text: "Alignment mode disabled. Joysticks and camera movement are unlocked.",
                duration: 5000
            });
        }
        
    }

    updateARAuthoringMenuVisibility(visible) {
        try {
            if (typeof mController === 'undefined') return;
            const menuData = mController.menuData.getThisMenuData('ARAuthoringMenu');
            if (menuData) {
                menuData.isVisible = !!visible;
                menuData.isPresenter = true;
                menuData.isViewer = false;
            }
            if (typeof menuVisibilityMap !== 'undefined') {
                menuVisibilityMap['ARAuthoringMenu'] = { isVisible: !!visible, isPresenter: true, isViewer: false };
            }
        } catch (e) {}
    }
}

const instance = new ARAuthoringController();

export default instance;
